import { viewportStretch } from "./layout-projection.js";
import { packIslands } from "./layout-components.js";
import { DEFAULT_LAYOUT_SETTINGS, sanitizeLayoutSettings } from "./layout-settings.js";
export const LAYOUT_PROFILE = "main-component-linlog-v2";
export const WARM_LAYOUT_VERSION = "component-fa2-cache-v1";
// [id, rendered x/y, 10 ForceAtlas floats, collision x/y/baseX/baseY, island dx/dy]
export function validWarmLayout(cache, nodeIds, settings = DEFAULT_LAYOUT_SETTINGS) {
  if (!cache || cache.version !== WARM_LAYOUT_VERSION || cache.profile !== LAYOUT_PROFILE || !Array.isArray(cache.rows) || !nodeIds.size) return false;
  if (!cache.settings || Object.keys(DEFAULT_LAYOUT_SETTINGS).some(key => !Number.isFinite(cache.settings[key]))) return false;
  const t = cache.transform;
  if (!t || ![t.x,t.y,t.cx,t.cy].every(Number.isFinite) || t.x <= 0 || t.y <= 0) return false;
  if (JSON.stringify(sanitizeLayoutSettings(cache.settings)) !== JSON.stringify(sanitizeLayoutSettings(settings))) return false;
  const seen = new Set();
  for (const row of cache.rows) {
    if (!Array.isArray(row) || row.length !== 19 || typeof row[0] !== "string" || seen.has(row[0]) || !row.slice(1).every(v => Number.isFinite(v) && Math.abs(v) < 1e12)) return false;
    seen.add(row[0]);
  }
  return [...nodeIds].filter(id => seen.has(id)).length / nodeIds.size >= .8;
}
export function restoreWarmEngine(engine, projection, cache) {
  engine.configure(cache.settings);
  engine.ids = cache.rows.map(r => r[0]);
  engine.index = new Map(engine.ids.map((id,i) => [id,i*10]));
  engine.nodes = new Float32Array(cache.rows.flatMap(r => r.slice(3,13)));
  engine.iterations = 720; engine.fastIterations = 0;
  projection.transform = { ...cache.transform };
  projection.previous = new Map(cache.rows.map(r => [r[0], { id:r[0], x:r[13], y:r[14], baseX:r[15], baseY:r[16] }]));
  // Reuse the same offset object across an island: merge/split anchor votes rely on identity.
  const offsets = new Map();
  projection.membership = new Map(cache.rows.map(r => {
    const key = `${r[17]}:${r[18]}`;
    if (!offsets.has(key)) offsets.set(key, {dx:r[17],dy:r[18]});
    return [r[0], offsets.get(key)];
  }));
}
export function captureWarmLayout(engine, projection, positions, metadata = {}) {
  const round = n => Math.round(n * 1000) / 1000;
  return { ...metadata, version:WARM_LAYOUT_VERSION, profile:LAYOUT_PROFILE, generatedAt:new Date().toISOString(),
    settings:{...engine.settings}, transform:{...projection.transform},
    rows: positions.map(p => {
      const i=engine.index.get(p.id), previous=projection.previous.get(p.id), offset=projection.membership.get(p.id) || {dx:0,dy:0};
      return [p.id, p.x,p.y, ...engine.nodes.subarray(i,i+10), previous.x,previous.y,previous.baseX,previous.baseY,offset.dx,offset.dy].map((v,i)=>i ? round(v) : v);
    }) };
}

// Adapt cached geometry once to this viewport, then repack island envelopes.
// Physics history is unchanged and no force iterations run on the main thread.
export function adaptWarmLayout(cache, viewport) {
  const result = { ...cache, transform: { ...cache.transform }, viewport, rows: cache.rows.map(row => [...row]) };
  if (!result.rows.length) return result;
  const xs = result.rows.map(r => r[1]), ys = result.rows.map(r => r[2]);
  const stretch = viewportStretch(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), viewport);
  result.transform.x *= stretch.x; result.transform.y *= stretch.y;
  // Render/collision coordinates follow the projection; the physical world and
  // velocity history do not change. Drag inversion therefore remains exact.
  for (const row of result.rows) {
    for (const i of [1, 13, 15, 17]) row[i] *= stretch.x;
    for (const i of [2, 14, 16, 18]) row[i] *= stretch.y;
  }
  const groups = new Map(), scale = cache.settings.displayScale;
  for (const row of result.rows) {
    const key = `${row[17]}:${row[18]}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  }
  const boxes = [...groups.values()].map(rows => {
    const gap = rows.length === 1 ? 5 : 22;
    const left = Math.min(...rows.map(r => r[1]-r[17] - (r[11]*scale+3))) - gap/2;
    const right = Math.max(...rows.map(r => r[1]-r[17] + (r[11]*scale+3))) + gap/2;
    const top = Math.min(...rows.map(r => r[2]-r[18] - (r[11]*scale+3))) - gap/2;
    const bottom = Math.max(...rows.map(r => r[2]-r[18] + (r[11]*scale+3))) + gap/2;
    return {id: rows.map(r=>r[0]).sort()[0],rows,left,top,width:right-left,height:bottom-top};
  });
  packIslands(boxes, viewport.width/viewport.height);
  for (const box of boxes) for (const row of box.rows) {
    row[1] += box.dx-row[17]; row[2] += box.dy-row[18]; row[17]=box.dx; row[18]=box.dy;
  }
  return result;
}
